/**
 * Narration Video Generation Service
 *
 * Handles video generation from uploaded narration audio.
 * The audio is transcribed using WhisperX and used to generate
 * a video that matches the narration timing exactly.
 */

import { RETRY_CONFIGS } from '../constants/timings';

import { handleError } from '../utils/errorHandler';
import { getAuthHeadersFromStorage } from '../utils/httpUtils';
import { firebaseClient } from './firebase/firebase-client';

/**
 * Video style options for narration videos
 */
export type NarrationVideoStyle =
  | 'professional'
  | 'cinematic'
  | 'educational'
  | 'dramatic'
  | 'documentary'
  | 'casual';

/**
 * Available video styles with descriptions
 */
export interface VideoStyleOption {
  id: NarrationVideoStyle;
  name: string;
  description: string;
}

/**
 * Request options for narration video generation
 */
export interface NarrationVideoOptions {
  videoStyle?: NarrationVideoStyle;
  resolution?: string;
  language?: string;
  imageSource?: 'stock' | 'ai';
}

/**
 * Response from narration video generation job submission
 */
export interface NarrationVideoSubmitResponse {
  success: boolean;
  data?: {
    job_id: string;
    status: string;
    message: string;
    video_style: string;
    resolution: string;
  };
  error?: string;
}

/**
 * Job status response
 */
export interface NarrationVideoJobStatus {
  success: boolean;
  data?: {
    job_id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress?: number;
    message?: string;
    result_url?: string;
    video_url?: string;
    error?: string;
    extra_metadata?: {
      detected_language?: string;
      segments_count?: number;
      sections_count?: number;
      video_style?: string;
      resolution?: string;
      duration_seconds?: number;
    };
  };
  error?: string;
}

/**
 * Video styles response
 */
export interface VideoStylesResponse {
  success: boolean;
  data?: {
    styles: VideoStyleOption[];
    default: NarrationVideoStyle;
  };
  error?: string;
}

/**
 * Narration limits response
 */
export interface NarrationLimitsResponse {
  success: boolean;
  data?: {
    max_file_size_mb: number;
    max_duration_minutes: number;
    supported_formats: string[];
  };
  error?: string;
}

// Polling configuration
const { maxAttempts: MAX_POLLING_ATTEMPTS, intervalMs: POLLING_INTERVAL_MS } =
  RETRY_CONFIGS.NARRATION_VIDEO;

/**
 * Narration Video API Service
 * Handles video generation from uploaded narration audio
 */
export class NarrationVideoAPI {
  /**
   * Get the base URL for the API
   */
  private static async getBaseUrl(): Promise<string> {
    const { VideoGenerationAPI } = await import('./api');
    return VideoGenerationAPI.getBaseUrl();
  }

  /**
   * Submit narration audio for video generation
   * @param file - Narration audio file
   * @param options - Video generation options
   * @param onProgress - Optional progress callback for upload
   * @returns Job submission response
   */
  static async submitNarrationVideo(
    file: File,
    options: NarrationVideoOptions = {},
    onProgress?: (progress: number) => void
  ): Promise<NarrationVideoSubmitResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const formData = new FormData();
      formData.append('file', file);

      if (options.videoStyle) {
        formData.append('video_style', options.videoStyle);
      }
      if (options.resolution) {
        formData.append('resolution', options.resolution);
      }
      if (options.language && options.language !== 'auto') {
        formData.append('language', options.language);
      }
      if (options.imageSource) {
        formData.append('image_source', options.imageSource);
      }

      // Get auth token before XHR promise
      const authToken = await firebaseClient.getIdToken();

      // Use XMLHttpRequest for upload progress
      const response = await new Promise<NarrationVideoSubmitResponse>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            // Upload progress is 0-30%
            const progress = Math.round((event.loaded / event.total) * 30);
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve({
                success: response.success === true,
                data: response.data,
                error: response.error,
              });
            } catch {
              reject(new Error('Failed to parse response'));
            }
          } else {
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              resolve({
                success: false,
                error: errorResponse.error || `Request failed with status ${xhr.status}`,
              });
            } catch {
              resolve({
                success: false,
                error: `Request failed with status ${xhr.status}`,
              });
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error occurred'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        xhr.open('POST', `${baseUrl}/api/v1/narration/generate-video`);
        if (authToken) {
          xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
        }
        xhr.send(formData);
      });

      return response;
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'NarrationVideo', 'Failed to submit narration video');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Poll job status until completion
   * @param jobId - Job ID to poll
   * @param onProgress - Optional progress callback
   * @returns Final job result
   */
  static async pollJobStatus(
    jobId: string,
    onProgress?: (progress: number, message?: string) => void
  ): Promise<NarrationVideoJobStatus> {
    const baseUrl = await this.getBaseUrl();
    let attempts = 0;

    while (attempts < MAX_POLLING_ATTEMPTS) {
      try {
        const response = await fetch(`${baseUrl}/api/v1/jobs/${jobId}`, {
          headers: await getAuthHeadersFromStorage(),
        });
        const data: NarrationVideoJobStatus = await response.json();

        if (!response.ok || !data.success) {
          return {
            success: false,
            error: data.error || 'Failed to get job status',
          };
        }

        const jobData = data.data;
        if (!jobData) {
          return {
            success: false,
            error: 'Invalid job status response',
          };
        }

        // Report progress
        if (onProgress) {
          // Progress is 30-100% (upload was 0-30%)
          const adjustedProgress = 30 + Math.round((jobData.progress || 0) * 0.7);
          onProgress(adjustedProgress, jobData.message);
        }

        // Check if job is complete
        if (jobData.status === 'completed') {
          if (onProgress) onProgress(100, 'Video generation complete');
          return data;
        }

        if (jobData.status === 'failed') {
          return {
            success: false,
            error: jobData.error || 'Narration video generation failed',
          };
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS));
        attempts++;
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling on transient errors
        await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL_MS));
        attempts++;
      }
    }

    return {
      success: false,
      error: 'Narration video generation timed out',
    };
  }

  /**
   * Generate video from narration audio (full flow)
   * @param file - Narration audio file
   * @param options - Video generation options
   * @param onProgress - Optional progress callback
   * @returns Final video result
   */
  static async generateVideo(
    file: File,
    options: NarrationVideoOptions = {},
    onProgress?: (progress: number, message?: string) => void
  ): Promise<NarrationVideoJobStatus> {
    // Submit the job
    const submitResponse = await this.submitNarrationVideo(file, options, (progress) => {
      if (onProgress) onProgress(progress, 'Uploading narration...');
    });

    if (!submitResponse.success || !submitResponse.data?.job_id) {
      return {
        success: false,
        error: submitResponse.error || 'Failed to submit narration video job',
      };
    }

    if (onProgress) onProgress(30, 'Processing narration...');

    // Poll for completion
    return this.pollJobStatus(submitResponse.data.job_id, onProgress);
  }

  /**
   * Get available video styles
   * @returns List of available video styles
   */
  static async getVideoStyles(): Promise<VideoStylesResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/narration/styles`, {
        headers: await getAuthHeadersFromStorage(),
      });
      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to fetch video styles',
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'NarrationVideo', 'Failed to fetch video styles');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Get narration file limits
   * @returns File size and duration limits
   */
  static async getNarrationLimits(): Promise<NarrationLimitsResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/narration/limits`, {
        headers: await getAuthHeadersFromStorage(),
      });
      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to fetch narration limits',
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'NarrationVideo', 'Failed to fetch narration limits');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Format duration in seconds to human readable string
   * @param seconds - Duration in seconds
   * @returns Formatted duration string
   */
  static formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (minutes > 0) {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  }
}

export default NarrationVideoAPI;
